import { useCallback, useMemo, useState } from "react";
import type { Job } from "../../../models";
import { JobsPriority } from "../../../utilities/types";
import { getNextPriorityInCycle, PRIORITY_REGULAR, PRIORITY_URGENT } from "./JobSelectionList.helpers";
import { JobSelectionViewModel } from "./JobSelectionList.types";

/**
 * Custom hook that encapsulates the business logic for job selection
 * @param jobs - All available jobs
 * @param selectedJobs - Currently selected jobs
 * @param onChange - Callback when selection changes
 * @returns ViewModel with all data and handlers
 */
export function useJobSelectionViewModel(
	jobs: Job[],
	selectedJobs: Job[],
	onChange: (jobs: Job[]) => void,
): JobSelectionViewModel {
	// Compute grouped and sorted jobs
	const groupedJobs = useMemo(() => {
		const grouped = jobs.reduce(
			(acc, job) => {
				const grower = job.growerName || "Unknown Grower";
				if (!acc[grower]) acc[grower] = [];
				acc[grower].push(job);
				return acc;
			},
			{} as Record<string, Job[]>,
		);

		// Sort jobs within each grower group
		Object.keys(grouped).forEach((growerName) => {
			grouped[growerName] = [...grouped[growerName]].sort((a, b) =>
				(a.fieldName || "").localeCompare(b.fieldName || ""),
			);
		});
		return grouped;
	}, [jobs]);

	const sortedGrowerNames = useMemo(() => Object.keys(groupedJobs).sort((a, b) => a.localeCompare(b)), [groupedJobs]);

	// Create a Set of selected job IDs for O(1) lookup instead of O(n)
	// Filter out any null/undefined values before mapping to IDs
	const selectedJobIds = useMemo(() => new Set(selectedJobs.filter((j) => !!j).map((j) => j.id)), [selectedJobs]);

	// Track which fields are visible for each job (independent of selection)
	const [expandedFields, setExpandedFields] = useState<Map<string, { showNotes: boolean; showPriority: boolean }>>(
		new Map(),
	);

	// Selection checker functions
	const isJobChecked = useCallback(
		(job: Job) => {
			return selectedJobIds.has(job.id);
		},
		[selectedJobIds],
	);

	const isGrowerFullySelected = useCallback(
		(growerName: string) => {
			const growerJobs = groupedJobs[growerName];
			return growerJobs.length > 0 && growerJobs.every((job) => selectedJobIds.has(job.id));
		},
		[groupedJobs, selectedJobIds],
	);

	const isGrowerPartiallySelected = useCallback(
		(growerName: string) => {
			const growerJobs = groupedJobs[growerName];
			const someSelected = growerJobs.some((job) => selectedJobIds.has(job.id));
			const allSelected = growerJobs.length > 0 && growerJobs.every((job) => selectedJobIds.has(job.id));
			return someSelected && !allSelected;
		},
		[groupedJobs, selectedJobIds],
	);

	const isJobExpanded = useCallback(
		(job: Job) => {
			// If job is not selected, it should not be expanded
			if (!isJobChecked(job)) return false;

			const fields = expandedFields.get(job.id);
			const notesVisible = fields?.showNotes || false;

			// Also check if priority is in a "green" state (should show dropdown)
			const selectedJob = selectedJobs.find((j) => j.id === job.id);
			const priority = selectedJob?.fieldPriority;
			const priorityVisible = !!(priority && priority !== PRIORITY_URGENT && priority !== PRIORITY_REGULAR);

			return notesVisible || priorityVisible;
		},
		[expandedFields, selectedJobs, isJobChecked],
	);

	const isNotesVisible = useCallback(
		(job: Job) => {
			return isJobChecked(job) && (expandedFields.get(job.id)?.showNotes ?? false);
		},
		[expandedFields, isJobChecked],
	);

	const isPriorityVisible = useCallback(
		(job: Job) => {
			if (!isJobChecked(job)) return false;

			const selectedJob = selectedJobs.find((j) => j.id === job.id);
			const priority = selectedJob?.fieldPriority;

			// Show dropdown only for priorities that are NOT Urgent or Regular Turn
			if (!priority) return false;
			return priority !== PRIORITY_URGENT && priority !== PRIORITY_REGULAR;
		},
		[selectedJobs, isJobChecked],
	);

	const getPriorityIconColor = useCallback(
		(job: Job): "error" | "warning" | "success" | undefined => {
			const selectedJob = selectedJobs.find((j) => j.id === job.id);
			const priority = selectedJob?.fieldPriority;

			if (!priority) return "warning"; // Default to yellow (Regular Turn)

			if (priority === PRIORITY_URGENT) return "error"; // Red
			if (priority === PRIORITY_REGULAR) return "warning"; // Yellow
			return "success"; // Green for all others
		},
		[selectedJobs],
	);

	const getPriorityTooltip = useCallback(
		(job: Job): string => {
			const selectedJob = selectedJobs.find((j) => j.id === job.id);
			const priority = selectedJob?.fieldPriority;

			return "Priority: " + (priority || PRIORITY_REGULAR) + "\nClick to change";
		},
		[selectedJobs],
	);

	const getSelectedJob = useCallback(
		(job: Job) => {
			return selectedJobs.find((selectedJob) => selectedJob.id === job.id);
		},
		[selectedJobs],
	);

	const hasSubmissionNotes = useCallback(
		(job: Job) => {
			const selectedJob = getSelectedJob(job);
			return !!selectedJob?.submissionNotes?.trim();
		},
		[getSelectedJob],
	);

	const hasCollectBoundary = useCallback(
		(job: Job) => {
			const selectedJob = getSelectedJob(job);
			return !!selectedJob?.collectBoundary;
		},
		[getSelectedJob],
	);

	// Event handlers
	const handleToggle = useCallback(
		(job: Job) => {
			const currentIndex = selectedJobs.filter((j) => !!j).findIndex((j) => j.id === job.id);
			const newValue = [...selectedJobs];

			if (currentIndex === -1) {
				// Job is being selected
				const jobToAdd = { ...job };

				// Set default priority if not already set
				if (!jobToAdd.fieldPriority) {
					jobToAdd.fieldPriority = PRIORITY_REGULAR;
				}

				newValue.push(jobToAdd);
			} else {
				// Job is being deselected - priority is preserved in the job object
				newValue.splice(currentIndex, 1);

				// Hide the notes text box when deselecting
				setExpandedFields((prev) => {
					const newMap = new Map(prev);
					const current = newMap.get(job.id);
					if (current) {
						newMap.set(job.id, { ...current, showNotes: false });
					}
					return newMap;
				});
			}

			onChange(newValue);
		},
		[selectedJobs, onChange],
	);

	const handleSelectAll = useCallback(() => {
		if (selectedJobs.length === jobs.length) {
			// Deselect all
			onChange([]);
		} else {
			// Select all
			onChange([...jobs]);
		}
	}, [jobs, selectedJobs.length, onChange]);

	const handleSelectAllForGrower = useCallback(
		(growerName: string) => {
			const growerJobs = groupedJobs[growerName];
			const allGrowerJobsSelected = growerJobs.every((job) =>
				selectedJobs.filter((j) => !!j).some((j) => j.id === job.id),
			);

			if (allGrowerJobsSelected) {
				// Deselect all jobs for this grower
				const newValue = selectedJobs
					.filter((job) => !!job)
					.filter((job) => !growerJobs.some((gj) => gj.id === job.id));
				onChange(newValue);
			} else {
				// Select all jobs for this grower
				const newValue = [...selectedJobs];
				growerJobs.forEach((job) => {
					if (!selectedJobs.filter((j) => !!j).some((j) => j.id === job.id)) {
						newValue.push(job);
					}
				});
				onChange(newValue);
			}
		},
		[groupedJobs, selectedJobs, onChange],
	);

	const getJobLabel = useCallback((job: Job): string => {
		if (job.farmName) {
			return `${job.fieldName} (${job.farmName})`;
		}
		return job.fieldName || "Unnamed Field";
	}, []);

	const handleNotesChange = useCallback(
		(job: Job, notes: string) => {
			const newValue = selectedJobs.map((selectedJob) => {
				if (selectedJob.id === job.id) {
					return { ...selectedJob, submissionNotes: notes };
				}
				return selectedJob;
			});
			onChange(newValue);

			// Also update the original job in the jobs array so values persist on deselect/reselect
			const originalJob = jobs.find((j) => j.id === job.id);
			if (originalJob) {
				originalJob.submissionNotes = notes;
			}
		},
		[selectedJobs, onChange, jobs],
	);

	const handlePriorityChange = useCallback(
		(job: Job, priority: JobsPriority) => {
			const newValue = selectedJobs.map((selectedJob) => {
				if (selectedJob.id === job.id) {
					return { ...selectedJob, fieldPriority: priority };
				}
				return selectedJob;
			});
			onChange(newValue);

			// Also update the original job in the jobs array so values persist on deselect/reselect
			const originalJob = jobs.find((j) => j.id === job.id);
			if (originalJob) {
				originalJob.fieldPriority = priority;
			}
		},
		[selectedJobs, onChange, jobs],
	);

	const handleNotesIconClick = useCallback((job: Job) => {
		setExpandedFields((prev) => {
			const newMap = new Map(prev);
			const current = newMap.get(job.id) || { showNotes: false, showPriority: false };

			// Toggle notes visibility
			newMap.set(job.id, {
				...current,
				showNotes: !current.showNotes,
			});

			return newMap;
		});
	}, []);

	const handlePriorityIconClick = useCallback(
		(job: Job) => {
			const selectedJob = selectedJobs.find((j) => j.id === job.id);
			const currentPriority = selectedJob?.fieldPriority;
			const nextPriority = getNextPriorityInCycle(currentPriority);

			// Update job priority using existing handler
			handlePriorityChange(job, nextPriority);
		},
		[selectedJobs, handlePriorityChange],
	);

	const handleCollectBoundaryToggle = useCallback(
		(job: Job) => {
			const newValue = selectedJobs.map((selectedJob) => {
				if (selectedJob.id === job.id) {
					return { ...selectedJob, collectBoundary: !selectedJob.collectBoundary };
				}
				return selectedJob;
			});
			onChange(newValue);

			// Also update the original job in the jobs array so values persist on deselect/reselect
			const originalJob = jobs.find((j) => j.id === job.id);
			if (originalJob) {
				originalJob.collectBoundary = !originalJob.collectBoundary;
			}
		},
		[selectedJobs, onChange, jobs],
	);

	// Derived state
	const isAllSelected = jobs.length > 0 && selectedJobs.length === jobs.length;
	const isPartiallySelected = selectedJobs.length > 0 && selectedJobs.length < jobs.length;

	return {
		groupedJobs,
		sortedGrowerNames,
		handleToggle,
		handleSelectAll,
		handleSelectAllForGrower,
		handleNotesChange,
		handlePriorityChange,
		handleNotesIconClick,
		handlePriorityIconClick,
		handleCollectBoundaryToggle,
		isJobChecked,
		isGrowerFullySelected,
		isGrowerPartiallySelected,
		isJobExpanded,
		isNotesVisible,
		isPriorityVisible,
		getJobLabel,
		getSelectedJob,
		getPriorityIconColor,
		getPriorityTooltip,
		hasSubmissionNotes,
		hasCollectBoundary,
		isAllSelected,
		isPartiallySelected,
	};
}
